import * as Select from "@radix-ui/react-select";
import { Icon } from "react-migration/components/Icon";
import { SiteStage } from "react-migration/domains/sites/typings/SiteStage";
import { SiteStageSelectItem } from "./SiteStageSelectItem";
import { StageDotIcon } from "./StageDotIcon";

export enum SiteStageSelectrTriggerType {
  Hatch = "hatch",
  Select = "select",
}

export type SiteStageSelectProps = Pick<Select.SelectProps, "disabled" | "value"> & {
  stages: SiteStage[];
  value?: string;
  onChange(value: string): void;
  triggerType?: SiteStageSelectrTriggerType;
};

export const SiteStageSelect = ({
  stages,
  value,
  onChange,
  triggerType = SiteStageSelectrTriggerType.Hatch,
  ...props
}: SiteStageSelectProps) => {
  const selected = stages.find((stage) => stage._id === value);
  const selectedStageColor = selected?.color ?? "#eee";
  const selectedStageName = selected?.name ?? "";

  return (
    <Select.Root onValueChange={onChange} value={value} {...props}>
      {triggerType === SiteStageSelectrTriggerType.Hatch ? (
        <HatchTrigger color={selectedStageColor} name={selectedStageName} />
      ) : (
        <SelectTrigger color={selectedStageColor} name={selectedStageName} />
      )}
      <Select.Portal>
        <Select.Content
          position="popper"
          side="bottom"
          sideOffset={4}
          className="z-10 flex flex-col p-3 overflow-hidden bg-white border rounded-lg shadow-md gap-y-2 w-60 max-h-72 border-blue-grey-200"
        >
          <div className="flex flex-col gap-x-1">
            <h1
              className="text-sm font-normal truncate cursor-default text-text-base-primary"
              title={selectedStageName}
            >
              {selectedStageName}
            </h1>
            <p className="text-xs cursor-default text-neutral-400">Move this site to another stage</p>
          </div>
          <Select.Viewport>
            <Select.Group className="max-h-52">
              {stages.map((stage) => (
                <SiteStageSelectItem key={stage._id} value={stage._id} color={stage.color} hidden={stage.hidden}>
                  {stage.name}
                </SiteStageSelectItem>
              ))}
            </Select.Group>
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

SiteStageSelect.TriggerType = SiteStageSelectrTriggerType;

interface SiteStageSelectTriggerProps {
  color: string;
  name: string;
}

const HatchTrigger = ({ color, name }: SiteStageSelectTriggerProps) => (
  <Select.Trigger style={{ background: color }} className="w-6 h-6 rounded-md shrink-0" title={name} />
);

const SelectTrigger = ({ color, name }: SiteStageSelectTriggerProps) => (
  <div className="w-full overflow-hidden">
    <Select.Trigger className="flex items-center w-full px-4 border border-solid rounded-md h-9 gap-x-3 border-blue-grey-200 focus:border-border-action-focus">
      <StageDotIcon color={color} />
      <span className="text-sm truncate select-none text-text-base-primary">{name}</span>
      <Icon icon="icon-lt-arrow-down-s-line" extraClasses="ml-auto pointer-events-none" />
    </Select.Trigger>
  </div>
);
